// Shift Detail takeover for the schedule-review side section. Rendered as a SideShell.Panel
// (header = "Shift Detail" + built-in back button) over the Review/Team/Activity tabs when a shift
// card is clicked in the grid. Reproduces the PR #6802 prototype FitPanel — shift header, the
// assigned-worker section, and the candidate lists. Times, the assigned worker's fit score, the
// "why this move" reasons and the candidate buckets all come from the real `scheduleReview` read
// path; the assignment actions mutate an in-session override on the page until the move mutations land.
import React from 'react';
import { Card, CardGroup, SideShell } from '@common/components';
import type {
  ScheduleDragStateType,
  ScheduleShiftDecisionType,
  ScheduleShiftOverrideType,
  ScheduleShiftType,
  ScheduleTeamMemberType,
} from '@load-balancing/types';
import type { ScheduleShiftCandidatesType } from '@load-balancing/graphql/queries/shift-candidates';
import { ScheduleShiftDetailHeader } from './_schedule-shift-detail-header';
import { ScheduleShiftDetailAssigned } from './_schedule-shift-detail-assigned';
import { ScheduleShiftDetailCandidates } from './_schedule-shift-detail-candidates';
import {
  assignedFitScore,
  classifyShift,
  enrichCandidateBuckets,
  shiftDurationHours,
  shiftTimeRange,
  whyThisMoveReasons,
} from './_schedule-shift-detail-panel-service';

export function ScheduleShiftDetailPanel({
  shift,
  dayLabel,
  dateLabel,
  bandLabel,
  bandTime,
  unitName,
  team,
  buckets,
  candidatesLoading = false,
  coverage,
  override,
  assigning = false,
  unassigning = false,
  onAssign,
  onUnassign,
  onDecision,
  onWorkerSelect,
  onWorkerDragStart,
  onWorkerDragEnd,
  readOnly = false,
}: {
  shift: ScheduleShiftType;
  dayLabel: string;
  dateLabel: string;
  bandLabel: string;
  /** Band default time ("07:00–19:00"); used when the shift carries no real instants. */
  bandTime: string;
  unitName?: string;
  team: ScheduleTeamMemberType[];
  /** Recomputed candidate buckets; null while loading or for an unknown/foreign shift. */
  buckets: ScheduleShiftCandidatesType | null;
  candidatesLoading?: boolean;
  /** Cell coverage base for the open-slot KPI chips; null when the cell has no demand. */
  coverage: { before: number; target: number } | null;
  override: ScheduleShiftOverrideType;
  /** An assignShift is in flight — disable the candidate Assign buttons. */
  assigning?: boolean;
  /** An unassignShift is in flight — show the Unassign button busy. */
  unassigning?: boolean;
  onAssign?: (memberId: string) => void;
  onUnassign?: () => void;
  onDecision?: (decision: ScheduleShiftDecisionType) => void;
  onWorkerSelect?: (memberId: string) => void;
  /** Drag a candidate onto an open grid slot (a new assignment). */
  onWorkerDragStart?: (state: ScheduleDragStateType) => void;
  onWorkerDragEnd?: () => void;
  readOnly?: boolean;
}) {
  const context = classifyShift(shift, override, team);
  // The effective assigned/suggested worker (balancer-move target, base worker, or in-session
  // manual assignment). Read their fit score off the raw buckets, then exclude them from the
  // enriched candidate list so they aren't offered as a candidate for their own shift.
  const assignedWorkerId = shift.assignedWorkerId ?? context.member?.id ?? null;
  const enrichedBuckets = buckets
    ? enrichCandidateBuckets(buckets, team, assignedWorkerId)
    : null;
  const fitScore = assignedFitScore(assignedWorkerId, buckets);
  const reasons = whyThisMoveReasons(shift);
  const isOpenSlot = context.kind === 'open_slot';

  return (
    <SideShell.Panel
      background
      header="Shift Detail"
      dataTestId="schedule-shift-detail-panel"
    >
      <CardGroup variant="joined">
        <Card>
          <ScheduleShiftDetailHeader
            dayLabel={dayLabel}
            dateLabel={dateLabel}
            bandLabel={bandLabel}
            timeRange={shiftTimeRange(shift, bandTime)}
            unitName={unitName}
            role={shift.role}
            requiredQualifications={shift.requiredQualifications}
          />
        </Card>
        <Card>
          <ScheduleShiftDetailAssigned
            context={context}
            unitName={unitName}
            role={shift.role}
            fitScore={fitScore}
            reasons={reasons}
            readOnly={readOnly}
            unassigning={unassigning}
            onUnassign={onUnassign}
            onDecision={onDecision}
            onWorkerSelect={onWorkerSelect}
          />
        </Card>
        <Card>
          {!readOnly && !context.isPto && (
            <ScheduleShiftDetailCandidates
              buckets={enrichedBuckets}
              loading={candidatesLoading}
              isOpenSlot={isOpenSlot}
              coverage={coverage}
              durationHours={shiftDurationHours(shift)}
              assigning={assigning}
              onAssign={onAssign}
              onWorkerSelect={onWorkerSelect}
              onWorkerDragStart={onWorkerDragStart}
              onWorkerDragEnd={onWorkerDragEnd}
            />
          )}
        </Card>
      </CardGroup>
    </SideShell.Panel>
  );
}
